import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import type { DashboardProcessosApi, DashboardProcessosQuery } from '../types/dashboardTypes';
import type {
  ArbitralExportStatusResponse,
  ExportProcessosArbitraisParams,
  FetchProcessosArbitraisParams,
  ListProcessosArbitraisResponse,
  SituacaoOption,
  SituacaoResumo,
  StartArbitralExportResponse,
} from '../types/processosArbitraisStoreTypes';

export const processosArbitraisService = {
  list: async (params?: FetchProcessosArbitraisParams) => {
    const queryParams: Record<string, string | number | boolean> = {};

    if (params?.search) {
      queryParams.search = params.search;
    }

    if (params?.tab) {
      queryParams.tab = params.tab;
    }

    if (params?.status && params.status.length > 0) {
      queryParams.status = params.status.join(',');
    }

    if (params?.rito && params.rito.length > 0) {
      queryParams.rito = params.rito.join(',');
    }

    if (params?.situacaoId && params.situacaoId.length > 0) {
      queryParams.situacaoId = params.situacaoId.join(',');
    }

    if (params?.inicioFluxoFalhou) {
      queryParams.inicioFluxoFalhou = true;
    }

    if (params?.dataEntradaInicio) {
      queryParams.dataEntradaInicio = params.dataEntradaInicio;
    }

    if (params?.dataEntradaFim) {
      queryParams.dataEntradaFim = params.dataEntradaFim;
    }

    if (params?.dataAtualizacaoInicio) {
      queryParams.dataAtualizacaoInicio = params.dataAtualizacaoInicio;
    }

    if (params?.dataAtualizacaoFim) {
      queryParams.dataAtualizacaoFim = params.dataAtualizacaoFim;
    }

    if (params?.paradoHaDias) {
      queryParams.paradoHaDias = params.paradoHaDias;
    }

    if (params?.page) {
      queryParams.page = params.page;
    }

    if (params?.limit) {
      queryParams.limit = params.limit;
    }

    if (params?.sortBy) {
      queryParams.sortBy = params.sortBy;
    }

    if (params?.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }

    const response = await apiGet('/arbitral/processos', queryParams);

    if (!response.ok) {
      throw new Error('Erro ao listar processos arbitrais');
    }

    const body = await response.json();
    return body.data as ListProcessosArbitraisResponse;
  },
  reprocessarInicio: async (processoId: string): Promise<void> => {
    const response = await apiPost(`/arbitral/processos/${processoId}/reprocessar-inicio`);

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { message?: string };
        message?: string;
      } | null;
      throw new Error(
        body?.error?.message ?? body?.message ?? 'Erro ao reprocessar o início do fluxo',
      );
    }
  },
  startExport: async (
    params: ExportProcessosArbitraisParams,
  ): Promise<StartArbitralExportResponse> => {
    const body = {
      search: params.search,
      tab: params.tab,
      status: params.status ?? [],
      rito: params.rito ?? [],
      ids: params.ids ?? [],
      dataEntradaInicio: params.dataEntradaInicio,
      dataEntradaFim: params.dataEntradaFim,
      somenteDaPlataforma: params.somenteDaPlataforma,
    };

    const response = await apiPost('/arbitral/processos/export', body);

    if (!response.ok) {
      throw new Error('Erro ao iniciar exportação de processos arbitrais');
    }

    return (await response.json()) as StartArbitralExportResponse;
  },
  dashboard: async (
    query: DashboardProcessosQuery,
    signal?: AbortSignal,
  ): Promise<DashboardProcessosApi> => {
    const response = await apiGet('/arbitral/processos/dashboard', { ...query }, false, signal);
    const body = (await response.json().catch(() => null)) as {
      data?: DashboardProcessosApi;
      error?: { message?: string };
      message?: string;
    } | null;

    if (!response.ok || !body?.data) {
      throw new Error(
        body?.error?.message ?? body?.message ?? 'Não foi possível carregar o dashboard',
      );
    }

    return body.data;
  },
  getExportStatus: async (exportId: string): Promise<ArbitralExportStatusResponse> => {
    const response = await apiGet(`/arbitral/processos/export/${exportId}`);

    if (!response.ok) {
      throw new Error('Erro ao consultar exportação de processos arbitrais');
    }

    return (await response.json()) as ArbitralExportStatusResponse;
  },
  listSituacaoOptions: async (): Promise<SituacaoOption[]> => {
    const [situacoesResponse, countsResponse] = await Promise.all([
      apiGet('/admin/arbitral/situacoes', { active: true }),
      apiGet('/admin/arbitral/painel/counts'),
    ]);

    if (!situacoesResponse.ok) {
      throw new Error('Erro ao carregar situações');
    }

    const situacoesBody = (await situacoesResponse.json()) as { items: SituacaoResumo[] };

    const countsById = new Map<string, number>();
    if (countsResponse.ok) {
      const countsBody = await countsResponse.json();
      const situacaoCounts = (countsBody?.data?.situacoes ?? []) as Array<{
        situacaoId: string;
        count: number;
      }>;
      situacaoCounts.forEach((entry) => countsById.set(entry.situacaoId, entry.count));
    }

    return situacoesBody.items.map((situacao) => ({
      ...situacao,
      count: countsById.get(situacao.id) ?? 0,
    }));
  },
};
